import {
  Checkbox,
  Divider,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { ValidationErrors, Validator } from 'fluentvalidation-ts';
import React, { useEffect } from 'react';
import { CustomSelect } from '../../../../Common/Components/Controls/InputControls';
import { CardWrapper } from '../../Common/CommonComponents';
import { MenuProps } from '../../DashBoard/Common/CommonConstants';
import { SqlServer } from '../../SqlServer/Model/SqlServerModel';
import { StorageAccount } from '../../StorageAccount/Model/StorageAccountModel';
import { AzureSearchService } from '../../SearchService/Model/AzureSearchServiceModel';
import { RegisterTenant, TenantForm } from '../Model/TenantModels';
import { AppserviceSkuDialog } from '../../../Common/Dialog/AppserviceSkuDialog';

interface TenantRegistrationFormProps {
  customerId: string;
  FarmId: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  registerTenant: RegisterTenant | undefined;
  storageAccounts: StorageAccount[];
  azureSearchServices: AzureSearchService[];
  sqlServers: SqlServer[];
  replicaServers: SqlServer[];
  appserviceplans: string[];
  storageAccount: string;
  azureSearchService: string;
  sqlServer: string;
  replicas: string[];
  handleReplicaSelection: (values: string[]) => void;
  setStorageAccount: React.Dispatch<React.SetStateAction<string>>;
  setAzureSearchService: React.Dispatch<React.SetStateAction<string>>;
  setSqlServer: React.Dispatch<React.SetStateAction<string>>;
  appserviceplan: string;
  setAppserviceplan: React.Dispatch<React.SetStateAction<string>>;
  handleClickOpen: () => void;
  setOpenAppserviceDialogue: React.Dispatch<React.SetStateAction<boolean>>;
  openAppserviceDialogue: boolean;
  selectedValue: string;
  handleCloseAppservicePlan: (value: string) => void;
  errors: ValidationErrors<TenantForm>;
  setErrors: React.Dispatch<React.SetStateAction<ValidationErrors<TenantForm>>>;
}

class FormValidator extends Validator<TenantForm> {
  constructor() {
    super();
    this.ruleFor('CustomerId')
      .notEmpty()
      .withMessage('customr Id cannot be empty');

    this.ruleFor('FarmId').notEmpty().withMessage('Farm Id cannot be empty');

    this.ruleFor('StorageAccount')
      .notEmpty()
      .withMessage('Storage Account cannot be empty');

    this.ruleFor('AzureSearchService')
      .notEmpty()
      .withMessage('Azure Search Service cannot be empty');

    this.ruleFor('AppservicePlan')
      .notEmpty()
      .withMessage('Appservice Plan cannot be empty');

    this.ruleFor('SqlServer')
      .notEmpty()
      .withMessage('Sql Server Cannot be empty');
  }
}

const TenantRegistrationForm = (props: TenantRegistrationFormProps) => {
  const {
    customerId,
    FarmId,
    handleChange,
    registerTenant,
    storageAccounts,
    azureSearchServices,
    appserviceplan,
    storageAccount,
    azureSearchService,
    sqlServer,
    replicas,
    setSqlServer,
    handleReplicaSelection,
    sqlServers,
    replicaServers,
    setStorageAccount,
    setAzureSearchService,
    appserviceplans,
    setAppserviceplan,
    handleClickOpen,
    openAppserviceDialogue,
    selectedValue,
    handleCloseAppservicePlan,
    errors,
    setErrors,
  } = props;
  const form: TenantForm = {
    CustomerId: customerId,
    FarmId: FarmId,
    StorageAccount: storageAccount,
    AzureSearchService: azureSearchService,
    AppservicePlan: appserviceplan,
    SqlServer: sqlServer,
    Replicas: replicas,
  };

  const formValidator = new FormValidator();

  const validate = () => {
    const formErrors = formValidator.validate(form);
    setErrors(formErrors);
  };

  useEffect(() => {
    validate();
  }, []);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <CardWrapper>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Tenant Informations
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Tenant Id</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">{form.CustomerId}</Typography>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Farm</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  error={!!errors.FarmId}
                  helperText={errors.FarmId}
                  value={form.FarmId}
                  placeholder="FarmId"
                  name="FarmId"
                  onBlur={validate}
                  onChange={handleChange}
                >
                  {registerTenant?.Farms.map((farm) => {
                    return (
                      <MenuItem
                        key={farm.Id.toString()}
                        value={farm.Id.toString()}
                      >
                        {farm.DisplayName}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Storage Account</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  disabled={
                    FarmId === '' || FarmId === undefined ? true : false
                  }
                  error={!!errors.StorageAccount}
                  helperText={errors.StorageAccount}
                  value={form.StorageAccount}
                  placeholder="StorageAccount"
                  name="StorageAccount"
                  onBlur={validate}
                  onChange={(e) => setStorageAccount(e.target.value)}
                  select
                >
                  {storageAccounts.map((sa) => {
                    return (
                      <MenuItem key={sa.Name} value={sa.Name}>
                        {sa.Name}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Azure Search Service</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  disabled={
                    FarmId === '' || FarmId === undefined ? true : false
                  }
                  error={!!errors.AzureSearchService}
                  helperText={errors.AzureSearchService}
                  value={form.AzureSearchService}
                  placeholder="AzureSearchService"
                  name="AzureSearchService"
                  onBlur={validate}
                  onChange={(e) => setAzureSearchService(e.target.value)}
                  select
                >
                  {azureSearchServices.map((sa) => {
                    return (
                      <MenuItem key={sa.Name} value={sa.Name}>
                        {sa.Name}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Sql Server</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  disabled={
                    FarmId === '' || FarmId === undefined ? true : false
                  }
                  error={!!errors.SqlServer}
                  helperText={errors.SqlServer}
                  value={form.SqlServer}
                  placeholder="SqlServer"
                  name="SqlServer"
                  onBlur={validate}
                  onChange={(e) => {
                    setSqlServer(e.target.value);
                    handleReplicaSelection([]);
                  }}
                  select
                >
                  {sqlServers.map((server) => {
                    return (
                      <MenuItem
                        key={server.ServerName}
                        value={server.ServerName}
                      >
                        {server.ServerName}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>
              </Grid>
            </Grid>
            {replicaServers && (
              <Grid container spacing={2} alignItems="center" mt={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1">Replicas</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <Select
                      fullWidth
                      disabled={
                        FarmId === '' || FarmId === undefined ? true : false
                      }
                      multiple
                      value={replicas || []}
                      onChange={(event) =>
                        handleReplicaSelection(event.target.value as string[])
                      }
                      inputProps={{ 'aria-label': 'Without label' }}
                      renderValue={(selected) => selected.join(', ')}
                      MenuProps={MenuProps}
                      onBlur={validate}
                    >
                      {replicaServers
                        .filter(
                          (server) => server.ServerName !== form.SqlServer,
                        )
                        .map((server) => (
                          <MenuItem
                            key={server.ServerName}
                            value={server.ServerName}
                          >
                            <Checkbox
                              checked={(replicas || []).includes(
                                server.ServerName,
                              )}
                            />
                            <ListItemText primary={server.ServerName} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2} alignItems="center" mt={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body1">Appservice Plan</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomSelect
                  disabled={
                    FarmId === '' || FarmId === undefined ? true : false
                  }
                  error={!!errors.AppservicePlan}
                  helperText={errors.AppservicePlan}
                  value={form.AppservicePlan}
                  placeholder="appserviceplan"
                  name="appserviceplan"
                  onBlur={validate}
                  onChange={(e) => setAppserviceplan(e.target.value)}
                  select
                >
                  <MenuItem
                    value={'Create New Appservice Plan'}
                    onClick={handleClickOpen}
                  >
                    Create New Appservice Plan
                  </MenuItem>
                  {appserviceplans.map((ap) => {
                    return (
                      <MenuItem key={ap} value={ap}>
                        {ap}
                      </MenuItem>
                    );
                  })}
                </CustomSelect>

                {openAppserviceDialogue && (
                  <AppserviceSkuDialog
                    onClose={handleCloseAppservicePlan}
                    selectedValue={selectedValue}
                    open={openAppserviceDialogue}
                  />
                )}
              </Grid>
            </Grid>
          </CardWrapper>
        </Grid>
      </Grid>
    </>
  );
};

export default TenantRegistrationForm;
